import React, { useState } from 'react';
import Box from '@amzn/awsui-components-core/box';
import Grid from '@amzn/awsui-components-core/grid';
import SpaceBetween from '@amzn/awsui-components-core/space-between';
import { GRID_TWO_COLUMN_EQUAL } from '../../utils/gridLayouts';
import { Image } from '../Image/Image';
import { LeadCapture } from '../LeadCapture/LeadCapture';
import { QuizAnsweredQuestion, QuizResultCard, resolveResultSlot } from './QuizResultSlots';
import { blockAnchorAttributes } from '../../utils/blockAnchor';
import './Quiz.scss';

/**
 * Quiz - Interactive quiz component for lead generation.
 *
 * Two views:
 *   • Quiz: a card with an orange progress bar, the current question, and
 *     pill-style answer options. Auto-advances on answer click in uncontrolled
 *     mode; can be driven by the `currentQuestion` prop. Supports up to 5
 *     questions with up to 4 answers each.
 *   • Results: shown when all questions are answered (or `showResults=true`).
 *     Hero + optional LeadCapture card + three result card slots.
 *
 * Result cards use a weighted slot model. Each of the three slots names the
 * questions that inform it via `resultCardNQuestions` (for example `"1,4"`), so
 * several questions can feed one slot and a quiz can ask up to five questions
 * while still presenting three cards. Those numbers are `question{N}` prop
 * numbers, so `"4"` means `question4` even when an earlier question slot is left
 * blank. The mapped answers select one of the slot's authored variants:
 * `resultCardNHeading` and friends are variant 1, and `resultCardNVariant2`–
 * `resultCardNVariant6` follow. Selection walks the mapped questions as
 * mixed-radix digits, so the last mapped question varies fastest — for questions
 * with 2 and 3 answers the variant order is
 * (1,1) (1,2) (1,3) (2,1) (2,2) (2,3).
 *
 * Those answer numbers are `Answer{M}` prop numbers too, so a blank answer slot
 * behaves like a blank question slot: `Answer4` keeps its place in the variant
 * order whether or not `Answer3` was filled. A blank slot below a filled one
 * therefore leaves a variant address that no answer can reach, and warns.
 *
 * A slot with no `resultCardNQuestions` mapping always renders variant 1, so
 * quizzes authored before weighted slots existed keep rendering unchanged.
 * There is no default ending: a mapped combination with no authored variant
 * renders no card and warns instead of substituting other content.
 */

export type QuizBackground = 'white' | 'off-white';

export interface QuizQuestion {
  question: string;
  answers: string[];
}

/** An answer the author filled in, tagged with the `Answer{M}` prop it came from. */
interface AuthoredAnswer {
  /** 1-based `Answer{M}` prop this answer was authored in. */
  answerNumber: number;
  text: string;
}

/**
 * A question the author actually filled in, tagged with the `question{N}` prop it
 * came from. Blank question slots are dropped, so a question's position in the
 * quiz stops matching its prop number as soon as an author leaves a gap — and a
 * result card mapping names the prop number, not the position.
 *
 * Answers carry their `Answer{M}` prop number for the same reason: only filled
 * answers render, but a blank slot still occupies an address in the variant order.
 */
interface AuthoredQuestion extends Omit<QuizQuestion, 'answers'> {
  /** 1-based `question{N}` prop this question was authored in. */
  questionNumber: number;
  answers: AuthoredAnswer[];
}

/**
 * Per-slot result card props for the CMS (slots 1-3, variants 1-6).
 *
 * Each slot's `resultCard{N}Questions` mapping names the `question{N}` props that
 * inform it; the base `resultCard{N}Heading`/`Description`/`ImageUrl`/`ImageAlt`
 * props are variant 1 and `Variant2`-`Variant6` follow.
 */
export interface QuizResultCardSlotProps {
  /**
   * Result card 1 — the `question{N}` prop numbers informing this slot, as
   * comma-separated numbers, e.g. `"1,4"`. Leave blank to always show variant 1.
   * @polyTranslatable false
   */
  resultCard1Questions?: string;
  resultCard1Heading?: string;
  resultCard1Description?: string;
  /** @polyType Url */
  resultCard1ImageUrl?: string;
  resultCard1ImageAlt?: string;
  resultCard1Variant2Heading?: string;
  resultCard1Variant2Description?: string;
  /** @polyType Url */
  resultCard1Variant2ImageUrl?: string;
  resultCard1Variant2ImageAlt?: string;
  resultCard1Variant3Heading?: string;
  resultCard1Variant3Description?: string;
  /** @polyType Url */
  resultCard1Variant3ImageUrl?: string;
  resultCard1Variant3ImageAlt?: string;
  resultCard1Variant4Heading?: string;
  resultCard1Variant4Description?: string;
  /** @polyType Url */
  resultCard1Variant4ImageUrl?: string;
  resultCard1Variant4ImageAlt?: string;
  resultCard1Variant5Heading?: string;
  resultCard1Variant5Description?: string;
  /** @polyType Url */
  resultCard1Variant5ImageUrl?: string;
  resultCard1Variant5ImageAlt?: string;
  resultCard1Variant6Heading?: string;
  resultCard1Variant6Description?: string;
  /** @polyType Url */
  resultCard1Variant6ImageUrl?: string;
  resultCard1Variant6ImageAlt?: string;

  /**
   * Result card 2 — the `question{N}` prop numbers informing this slot, as
   * comma-separated numbers, e.g. `"2,5"`. Leave blank to always show variant 1.
   * @polyTranslatable false
   */
  resultCard2Questions?: string;
  resultCard2Heading?: string;
  resultCard2Description?: string;
  /** @polyType Url */
  resultCard2ImageUrl?: string;
  resultCard2ImageAlt?: string;
  resultCard2Variant2Heading?: string;
  resultCard2Variant2Description?: string;
  /** @polyType Url */
  resultCard2Variant2ImageUrl?: string;
  resultCard2Variant2ImageAlt?: string;
  resultCard2Variant3Heading?: string;
  resultCard2Variant3Description?: string;
  /** @polyType Url */
  resultCard2Variant3ImageUrl?: string;
  resultCard2Variant3ImageAlt?: string;
  resultCard2Variant4Heading?: string;
  resultCard2Variant4Description?: string;
  /** @polyType Url */
  resultCard2Variant4ImageUrl?: string;
  resultCard2Variant4ImageAlt?: string;
  resultCard2Variant5Heading?: string;
  resultCard2Variant5Description?: string;
  /** @polyType Url */
  resultCard2Variant5ImageUrl?: string;
  resultCard2Variant5ImageAlt?: string;
  resultCard2Variant6Heading?: string;
  resultCard2Variant6Description?: string;
  /** @polyType Url */
  resultCard2Variant6ImageUrl?: string;
  resultCard2Variant6ImageAlt?: string;

  /**
   * Result card 3 — the `question{N}` prop numbers informing this slot, as
   * comma-separated numbers, e.g. `"3"`. Leave blank to always show variant 1.
   * @polyTranslatable false
   */
  resultCard3Questions?: string;
  resultCard3Heading?: string;
  resultCard3Description?: string;
  /** @polyType Url */
  resultCard3ImageUrl?: string;
  resultCard3ImageAlt?: string;
  resultCard3Variant2Heading?: string;
  resultCard3Variant2Description?: string;
  /** @polyType Url */
  resultCard3Variant2ImageUrl?: string;
  resultCard3Variant2ImageAlt?: string;
  resultCard3Variant3Heading?: string;
  resultCard3Variant3Description?: string;
  /** @polyType Url */
  resultCard3Variant3ImageUrl?: string;
  resultCard3Variant3ImageAlt?: string;
  resultCard3Variant4Heading?: string;
  resultCard3Variant4Description?: string;
  /** @polyType Url */
  resultCard3Variant4ImageUrl?: string;
  resultCard3Variant4ImageAlt?: string;
  resultCard3Variant5Heading?: string;
  resultCard3Variant5Description?: string;
  /** @polyType Url */
  resultCard3Variant5ImageUrl?: string;
  resultCard3Variant5ImageAlt?: string;
  resultCard3Variant6Heading?: string;
  resultCard3Variant6Description?: string;
  /** @polyType Url */
  resultCard3Variant6ImageUrl?: string;
  resultCard3Variant6ImageAlt?: string;
}

/**
 * @polySemanticUsage About this block: an interactive, multi-step question-and-answer flow that
 * guides users to a personalized result or recommendation. Use it for product/plan selectors,
 * readiness assessments, or guided decision-making experiences that increase engagement and lead
 * qualification.
 */
export interface QuizProps extends QuizResultCardSlotProps {
  /** Quiz heading (Quiz stage) */
  heading: string;

  /** Quiz description */
  description?: string;

  /** Question 1 */
  question1: string;
  question1Answer1: string;
  question1Answer2?: string;
  question1Answer3?: string;
  question1Answer4?: string;

  /** Question 2 */
  question2?: string;
  question2Answer1?: string;
  question2Answer2?: string;
  question2Answer3?: string;
  question2Answer4?: string;

  /** Question 3 */
  question3?: string;
  question3Answer1?: string;
  question3Answer2?: string;
  question3Answer3?: string;
  question3Answer4?: string;

  /** Question 4 */
  question4?: string;
  question4Answer1?: string;
  question4Answer2?: string;
  question4Answer3?: string;
  question4Answer4?: string;

  /** Question 5 */
  question5?: string;
  question5Answer1?: string;
  question5Answer2?: string;
  question5Answer3?: string;
  question5Answer4?: string;

  /**
   * Current question index (0-based, controlled).
   *
   * Programmatic only — a parent supplies this at runtime, authors never do.
   * @polyIgnore
   */
  currentQuestion?: number;

  /**
   * Selected answer index for each question, in the order the quiz asks them
   * (controlled). The outer position is positional, because blank `question{N}`
   * slots are skipped — unlike `resultCardNQuestions`, which names prop numbers.
   * Each value is the 0-based `Answer{M}` prop slot, so a blank earlier answer
   * does not shift it.
   *
   * A controlled quiz never captures answers internally, so this is required
   * alongside `currentQuestion` for results to personalize.
   *
   * Programmatic only — a parent supplies this at runtime, authors never do.
   * @polyIgnore
   */
  selectedAnswers?: number[];

  /**
   * Callback when an answer is selected. `answerIndex` is the 0-based `Answer{M}`
   * prop slot the visitor chose, not its position among the rendered answers.
   */
  onAnswer?: (questionIndex: number, answerIndex: number, answerText: string) => void;

  /** Callback fired once when the last question is answered (uncontrolled mode) */
  onQuizComplete?: (selectedAnswers: number[]) => void;

  /**
   * Controlled: force the results view (overrides internal state).
   *
   * Programmatic only — a parent supplies this at runtime, authors never do.
   * @polyIgnore
   */
  showResults?: boolean;

  /** Results hero heading (defaults to `heading` when omitted) */
  resultsHeading?: string;

  /** Results hero description */
  resultsDescription?: string;

  /** Lead capture card heading. The lead-capture card renders only when set. */
  leadCaptureHeading?: string;

  /** Lead capture card subheading */
  leadCaptureDescription?: string;

  /** Business-email input placeholder */
  emailPlaceholder?: string;

  /** Lead capture submit button label */
  emailSubmitLabel?: string;

  /**
   * Endpoint the lead-capture form POSTs the business email and website to
   * on submit. Ignored when `onEmailSubmit` is set. Without either, the form
   * shows its error state, because the lead has nowhere to go.
   * @polyType Url
   * @polyLabel Lead Capture Submit Endpoint
   */
  submitEndpoint?: string;

  /**
   * Content-type used when POSTing to `submitEndpoint`. Use `form` for Pardot
   * Form Handlers, Salesforce Web-to-Lead and most legacy CRMs; use `json`
   * for custom REST endpoints. Defaults to `form`.
   * @polyType SelectList
   * @polyLabel Lead Capture Submit Format
   * @polyOptions Form (Pardot / legacy CRM)=form,JSON (custom REST API)=json
   * @polyDefault form
   */
  submitFormat?: 'form' | 'json';

  /**
   * Called with the submitted email instead of posting to `submitEndpoint`.
   * Programmatic delivery path; when set, `submitEndpoint` is not used, so a
   * lead is never delivered twice.
   */
  onEmailSubmit?: (email: string) => void;

  /** Heading shown after a successful lead-capture submit */
  emailSuccessMessage?: string;

  /**
   * Background style preset
   * - `'white'`: White background (default)
   * - `'off-white'`: Off-white background
   */
  background?: QuizBackground;

  /**
   * Accessible name for the quiz region. Translatable so screen readers
   * announce it in the page language.
   * @polyLabel Screen Reader Label
   * @polyHelp Name screen readers announce for the quiz. Translated with the page.
   * @polyDefault Quiz
   */
  ariaLabel?: string;

  /**
   * Accessible name for the results region.
   * @polyLabel Results Screen Reader Label
   * @polyHelp Name screen readers announce for the results view. Translated with the page.
   * @polyDefault Quiz results
   */
  resultsAriaLabel?: string;
  /**
   * Anchor ID for in-page links: a link to `#<anchorId>` on the same page scrolls to this block.
   * Normalised to letters, numbers and dashes (spaces become dashes, a leading `#` and other
   * characters are dropped). Must be unique on the page. Migration: when any link on the source page points at `#<id>` and that id sits on the
   * source section this block replaces, copy the id here unchanged (without the `#`).
   * @polyType Text
   * @polyLabel Anchor ID
   * @polyHelp Lets in-page links such as #your-id jump to this block. Use letters, numbers and dashes (other characters are removed); must be unique on the page.
   * @polyTranslatable false
   */
  anchorId?: string;
}

const DEFAULT_ARIA_LABEL = 'Quiz';
const DEFAULT_RESULTS_ARIA_LABEL = 'Quiz results';

/**
 * Maps background preset values to Poly design tokens
 */
const getBackgroundColor = (background?: QuizBackground): string => {
  const backgroundMap: Record<QuizBackground, string> = {
    'white': 'var(--poly-color-background-white)',
    'off-white': 'var(--poly-color-background-offWhite)',
  };

  if (!background) return backgroundMap['white'];

  const color = backgroundMap[background];
  if (!color) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn(`[Quiz] Unknown background preset: "${background}". Falling back to "white".`);
    }
    return backgroundMap['white'];
  }

  return color;
};

/** Slots available on the results view. */
type QuizResultSlot = 1 | 2 | 3;

/** Variant positions authored beyond variant 1, which lives on the base card props. */
const ADDITIONAL_VARIANTS = [2, 3, 4, 5, 6] as const;

/** Tags each answer with its `Answer{M}` prop number and drops the blank slots. */
const authorAnswers = (answers: (string | undefined)[]): AuthoredAnswer[] =>
  answers
    .map((text, index) => ({ text, answerNumber: index + 1 }))
    .filter((answer): answer is AuthoredAnswer => !!answer.text);

/**
 * How many answer addresses a question spans — its highest filled `Answer{M}` slot.
 *
 * Using the highest slot rather than the number of filled answers is what keeps
 * `Answer4` selecting variant 4 whether or not `Answer3` was filled.
 */
const countAnswerSlots = (answers: AuthoredAnswer[]): number =>
  answers.reduce((highest, answer) => Math.max(highest, answer.answerNumber), 0);

/** The `Answer{M}` slots left blank below a filled one. */
const findBlankAnswerSlots = (answers: AuthoredAnswer[]): number[] => {
  const authored = new Set(answers.map(answer => answer.answerNumber));

  return Array.from({ length: countAnswerSlots(answers) }, (_, index) => index + 1).filter(
    answerNumber => !authored.has(answerNumber)
  );
};

/**
 * Warns about an answer slot left blank below a filled one.
 *
 * The blank still occupies an address in the variant order, so a variant authored
 * at that address can never render — no answer selects it.
 */
const warnOnBlankAnswerSlots = (question: AuthoredQuestion): void => {
  if (process.env.NODE_ENV === 'production') return;

  for (const answerNumber of findBlankAnswerSlots(question.answers)) {
    console.warn(
      `[Quiz] Question ${question.questionNumber} leaves answer ${answerNumber} blank but fills a later one. ` +
        `The blank still occupies an address in the variant order, so some variants of any result card ` +
        `mapped to this question can never render — renumber the answers to close the gap.`
    );
  }
};

/**
 * Warns about a question authored with a heading but no answers.
 *
 * Such a question survives the heading-only filter, renders its heading and
 * progress bar, and renders zero answer buttons — so an uncontrolled quiz can never
 * advance past it. Called before either stage branch rather than only on the quiz
 * stage, so an author who lands straight on a controlled results view still hears
 * about it.
 */
const warnOnQuestionsWithNoAnswers = (question: AuthoredQuestion): void => {
  if (process.env.NODE_ENV === 'production') return;

  if (question.answers.length === 0) {
    console.warn(
      `[Quiz] Question ${question.questionNumber} has no answers authored, so it renders no ` +
        `answer buttons and an uncontrolled quiz cannot advance past it. Fill at least one ` +
        `question${question.questionNumber}Answer{M} slot, or clear the question heading to ` +
        `drop the slot entirely.`
    );
  }
};

/** Collects the card variants authored for a result slot, ordered from variant 1. */
function getSlotVariants(props: QuizResultCardSlotProps, slot: QuizResultSlot): QuizResultCard[] {
  return [
    {
      heading: props[`resultCard${slot}Heading`],
      description: props[`resultCard${slot}Description`],
      imageUrl: props[`resultCard${slot}ImageUrl`],
      imageAlt: props[`resultCard${slot}ImageAlt`],
    },
    ...ADDITIONAL_VARIANTS.map((variant) => ({
      heading: props[`resultCard${slot}Variant${variant}Heading`],
      description: props[`resultCard${slot}Variant${variant}Description`],
      imageUrl: props[`resultCard${slot}Variant${variant}ImageUrl`],
      imageAlt: props[`resultCard${slot}Variant${variant}ImageAlt`],
    })),
  ];
}

export const Quiz: React.FC<QuizProps> = (props) => {
  const {
    heading,
    description,
    question1, question1Answer1, question1Answer2, question1Answer3, question1Answer4,
    question2, question2Answer1, question2Answer2, question2Answer3, question2Answer4,
    question3, question3Answer1, question3Answer2, question3Answer3, question3Answer4,
    question4, question4Answer1, question4Answer2, question4Answer3, question4Answer4,
    question5, question5Answer1, question5Answer2, question5Answer3, question5Answer4,
    currentQuestion: controlledQuestion,
    selectedAnswers: controlledAnswers,
    onAnswer,
    onQuizComplete,
    showResults: controlledShowResults,
    resultsHeading,
    resultsDescription,
    leadCaptureHeading,
    leadCaptureDescription,
    emailPlaceholder = 'Enter your business email',
    emailSubmitLabel = 'Subscribe now',
    submitEndpoint,
    submitFormat,
    onEmailSubmit,
    emailSuccessMessage = 'Thanks — check your inbox.',
    background,
    ariaLabel,
    resultsAriaLabel,
  } = props;

  // Reassemble questions from flat props
  const questions: AuthoredQuestion[] = [
    { questionNumber: 1, question: question1, answers: [question1Answer1, question1Answer2, question1Answer3, question1Answer4] },
    { questionNumber: 2, question: question2, answers: [question2Answer1, question2Answer2, question2Answer3, question2Answer4] },
    { questionNumber: 3, question: question3, answers: [question3Answer1, question3Answer2, question3Answer3, question3Answer4] },
    { questionNumber: 4, question: question4, answers: [question4Answer1, question4Answer2, question4Answer3, question4Answer4] },
    { questionNumber: 5, question: question5, answers: [question5Answer1, question5Answer2, question5Answer3, question5Answer4] },
  ]
    .map(q => ({ ...q, answers: authorAnswers(q.answers) }))
    .filter((q): q is AuthoredQuestion => !!q.question);

  questions.forEach(warnOnQuestionsWithNoAnswers);

  const [internalQuestion, setInternalQuestion] = useState(0);
  const [internalAnswers, setInternalAnswers] = useState<number[]>([]);
  const [internalComplete, setInternalComplete] = useState(false);

  // Use controlled or internal state
  const currentIndex = controlledQuestion !== undefined ? controlledQuestion : internalQuestion;
  const showResults = controlledShowResults !== undefined ? controlledShowResults : internalComplete;

  const currentQ = questions[currentIndex];
  const progress = questions.length > 0 ? ((currentIndex + 1) / questions.length) * 100 : 0;

  const handleAnswer = (answer: AuthoredAnswer) => {
    if (!currentQ) return;
    const answerIndex = answer.answerNumber - 1;

    if (onAnswer) {
      onAnswer(currentIndex, answerIndex, answer.text);
    }

    if (controlledQuestion === undefined) {
      const nextAnswers = [...internalAnswers];
      nextAnswers[currentIndex] = answerIndex;
      setInternalAnswers(nextAnswers);

      if (currentIndex < questions.length - 1) {
        setInternalQuestion(currentIndex + 1);
      } else {
        setInternalComplete(true);
        if (onQuizComplete) onQuizComplete(nextAnswers);
      }
    }
  };

  const resolvedBackgroundColor = getBackgroundColor(background);

  // Data guards
  if (!showResults && questions.length === 0) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn('[Quiz] No questions provided. Component will not render.');
    }
    return (
      <div
        data-poly-error="quiz-no-questions"
        data-poly-error-detail="No question{N} slots populated"
        aria-hidden="true"
        hidden
      />
    );
  }

  // -- RESULTS STAGE --
  if (showResults) {
    const selectedAnswers = controlledAnswers ?? internalAnswers;
    questions.forEach(warnOnBlankAnswerSlots);

    const answeredQuestions: QuizAnsweredQuestion[] = questions.map((question, position) => ({
      questionNumber: question.questionNumber,
      answerSlots: countAnswerSlots(question.answers),
      answer: selectedAnswers[position],
    }));

    const resultCards = ([1, 2, 3] as const)
      .map(slot =>
        resolveResultSlot({
          mapping: props[`resultCard${slot}Questions`],
          variants: getSlotVariants(props, slot),
          questions: answeredQuestions,
          slotNumber: slot,
        })
      )
      .filter((card): card is QuizResultCard => card !== undefined);

    return (
      <section
        {...blockAnchorAttributes(props.anchorId)}
        className="quiz quiz--results"
        style={{ backgroundColor: resolvedBackgroundColor }}
        aria-label={resultsAriaLabel || DEFAULT_RESULTS_ARIA_LABEL}
      >
        <div className="quiz-results">
          {(resultsHeading || heading || resultsDescription) && (
            <header className="quiz-results__hero">
              <h2 className="quiz-results__hero-heading">{resultsHeading || heading}</h2>
              {resultsDescription && (
                <p className="quiz-results__hero-description">{resultsDescription}</p>
              )}
            </header>
          )}

          {leadCaptureHeading && (
            <LeadCapture
              variant="card"
              fields="email-website"
              heading={leadCaptureHeading}
              subheading={leadCaptureDescription}
              websitePlaceholder="Enter your business website"
              emailLabel="Business email"
              emailPlaceholder={emailPlaceholder}
              submitCtaText={emailSubmitLabel}
              submitCtaColorVariant="secondary"
              incentivesText="Opt out anytime."
              successHeading={emailSuccessMessage}
              submitEndpoint={onEmailSubmit ? undefined : submitEndpoint}
              submitFormat={submitFormat}
              onSubmit={onEmailSubmit ? (payload) => onEmailSubmit(payload.email) : undefined}
            />
          )}

          {resultCards.length > 0 && (
            <div
              className="quiz-results__cards"
              data-card-count={resultCards.length}
              role="list"
            >
              {resultCards.map((card, index) => (
                <article
                  key={`quiz-result-${index}`}
                  className="quiz-results__card"
                  role="listitem"
                >
                  {card.imageUrl && (
                    <div className="quiz-results__card-media">
                      <Image
                        bare
                        url={card.imageUrl}
                        alt={card.imageAlt || ''}
                        loading="lazy"
                      />
                    </div>
                  )}
                  <div className="quiz-results__card-body">
                    {card.heading && (
                      <h4 className="quiz-results__card-heading">{card.heading}</h4>
                    )}
                    {card.description && (
                      <p className="quiz-results__card-description">{card.description}</p>
                    )}
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>
    );
  }

  // -- QUIZ STAGE --
  const leftColumn = (
    <Box padding={{ vertical: 'xl', horizontal: 'l' }}>
      <SpaceBetween size="l">
        <div>
          <h2 className="quiz__heading">{heading}</h2>
          {description && (
            <p className="quiz__description">{description}</p>
          )}
        </div>
      </SpaceBetween>
    </Box>
  );

  const rightColumn = (
    <div className="quiz-card">
      <SpaceBetween size="l">
        <div className="quiz__progress">
          <span className="quiz__progress-label">
            Question {currentIndex + 1} of {questions.length}
          </span>
          <div className="quiz__progress-bar">
            <div
              className="quiz__progress-bar-fill"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        <h3 className="quiz__question">{currentQ.question}</h3>
      </SpaceBetween>

      <div className="quiz__answer-section">
        {currentQ.answers.map((answer) => (
          <button
            key={answer.answerNumber}
            type="button"
            className="quiz__answer-pill"
            onClick={() => handleAnswer(answer)}
          >
            {answer.text}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <section
      {...blockAnchorAttributes(props.anchorId)}
      className="quiz"
      style={{ backgroundColor: resolvedBackgroundColor }}
      aria-label={ariaLabel || DEFAULT_ARIA_LABEL}
    >
      <Box padding={{ vertical: 'xl', horizontal: 'l' }}>
        <Grid gridDefinition={GRID_TWO_COLUMN_EQUAL}>
          <div>{leftColumn}</div>
          <div>{rightColumn}</div>
        </Grid>
      </Box>
    </section>
  );
};
